import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useMemo } from 'react';
import CALENDAR_VIEW_TYPES from 'ui-addon-calendars/constants/CalendarViewTypes';
import { OverflowPopover } from './OverflowPopover';
import { OverflowButton } from './OverflowButton';
import { useCalendarProps } from '../../internal/CalendarPropsProvider';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const useRenderOverflowEvents = (renderOverflowEvents, args) => {
  return useMemo(() => {
    if (!renderOverflowEvents) {
      return undefined;
    }
    return () => renderOverflowEvents(args);
  }, [renderOverflowEvents, args]);
};
const DisplayOverflow = ({
  showOverflow,
  button,
  renderOverflowEvents,
  isOpen,
  onClose,
  allEvents,
  actualDate
}) => {
  if (!showOverflow) {
    return null;
  }
  return renderOverflowEvents ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [isOpen ? renderOverflowEvents() : null, button]
  }) : /*#__PURE__*/_jsx(OverflowPopover, {
    open: isOpen,
    onClose: onClose,
    overflowEvents: allEvents,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "calendar.weekDayDate",
      options: {
        weekDay: actualDate.format('dddd'),
        date: actualDate.date()
      }
    }),
    children: button
  });
};
export const MonthOverflow = ({
  actualDate,
  buttonAs,
  dayOfWeek,
  dayIndex,
  displayDay,
  displayMonth,
  displayYear,
  events,
  hideWeekends,
  onOverflowButtonClick,
  onPopoverClose,
  openPopover,
  renderOverflowEvents,
  useExactEventTimes,
  week
}) => {
  const {
    timezone
  } = useCalendarProps();
  const {
    overflowCount,
    events: allEvents
  } = events.get(week).get(dayOfWeek);
  const isOpen = (openPopover === null || openPopover === void 0 ? void 0 : openPopover.week) === week && (openPopover === null || openPopover === void 0 ? void 0 : openPopover.dayOfWeek) === dayOfWeek;
  const showOverflow = overflowCount > 0;
  const renderOverflow = useRenderOverflowEvents(renderOverflowEvents, {
    date: actualDate,
    dayOfWeek,
    displayDay,
    displayMonth,
    displayYear,
    hideWeekends,
    hour: -1,
    onClose: onPopoverClose,
    timezone,
    useExactEventTimes,
    viewType: CALENDAR_VIEW_TYPES.MONTH,
    week
  });
  const handleClick = useCallback(() => {
    onOverflowButtonClick({
      week,
      dayOfWeek,
      overflowCount,
      dayColumnIndex: dayIndex
    });
  }, [onOverflowButtonClick, week, dayOfWeek, overflowCount, dayIndex]);
  return /*#__PURE__*/_jsx(DisplayOverflow, {
    showOverflow: showOverflow,
    button: /*#__PURE__*/_jsx(OverflowButton, {
      as: buttonAs,
      buttonProps: {
        dayColumnIndex: dayIndex
      },
      onClick: handleClick,
      overflowCount: overflowCount,
      "data-test-id": `events-overflow-button--${week}-${dayIndex}`
    }),
    renderOverflowEvents: renderOverflow,
    isOpen: isOpen,
    onClose: onPopoverClose,
    allEvents: allEvents,
    actualDate: actualDate
  });
};
export const WeekOverflow = ({
  actualDate,
  buttonAs,
  dayOfWeek,
  displayDay,
  displayMonth,
  displayYear,
  events,
  hideWeekends,
  onOverflowButtonClick,
  onPopoverClose,
  openPopover,
  renderOverflowEvents,
  useExactEventTimes,
  hour,
  maxEventDisplayCount
}) => {
  const {
    timezone
  } = useCalendarProps();
  const eventsForHour = events.getIn([dayOfWeek, hour]);
  const numberOfEvents = eventsForHour.size;
  const overflowCount = numberOfEvents - maxEventDisplayCount;
  const isOpen = (openPopover === null || openPopover === void 0 ? void 0 : openPopover.hour) === hour && (openPopover === null || openPopover === void 0 ? void 0 : openPopover.dayOfWeek) === dayOfWeek;
  const showOverflow = overflowCount > 0;
  const renderOverflow = useRenderOverflowEvents(renderOverflowEvents, {
    date: actualDate,
    dayOfWeek,
    displayDay,
    displayMonth,
    displayYear,
    hideWeekends,
    hour,
    week: -1,
    onClose: onPopoverClose,
    timezone,
    useExactEventTimes,
    viewType: CALENDAR_VIEW_TYPES.WEEK
  });
  const handleClick = useCallback(() => {
    onOverflowButtonClick({
      hour,
      dayOfWeek,
      overflowCount
    });
  }, [onOverflowButtonClick, hour, dayOfWeek, overflowCount]);
  return /*#__PURE__*/_jsx(DisplayOverflow, {
    showOverflow: showOverflow,
    button: /*#__PURE__*/_jsx(OverflowButton, {
      as: buttonAs,
      onClick: handleClick,
      overflowCount: overflowCount,
      "data-test-id": `events-overflow-button--${dayOfWeek}-${hour}`
    }),
    renderOverflowEvents: renderOverflow,
    isOpen: isOpen,
    onClose: onPopoverClose,
    allEvents: eventsForHour,
    actualDate: actualDate
  });
};